import { useEffect, useState } from "react";
import axios from "axios";
import { X, Save, AlertCircle, CalendarDays, Users } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const SERVICE_OPTIONS = [
  "NDIS Support Coordination",
  "Psychosocial Recovery Coaching (PSRC)",
  "Early Childhood Approach Supports (Ages 0 to 9)",
  "DVA Social Work & Wellbeing Coordination",
  "Domestic & Family Violence Social Work",
  "Sport & Performance Social Work",
];

const TEAM_OPTIONS = [
  { value: "ndis", label: "NDIS" },
  { value: "dva", label: "DVA" },
  { value: "dfv", label: "D&FV (Domestic & Family Violence)" },
  { value: "community", label: "Community Events" },
  { value: "other", label: "Other" },
];

const BILLING_RECIPIENT_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "ndia", label: "NDIA (Agency managed)" },
  { value: "plan_manager", label: "Plan Manager" },
  { value: "self_managed", label: "Self managed (the client)" },
  { value: "other", label: "Other" },
];

const EVENT_BILLING_OPTIONS = [
  { value: "", label: "Not billed / internal" },
  { value: "other", label: "Grant body / sponsor / partner org" },
  { value: "ndia", label: "NDIA Community Connection funding" },
];

const EVENT_TYPES = [
  "Wellbeing BBQ",
  "Workshop",
  "Information Session",
  "Mental Health Day",
  "Peer Support Group",
  "Community Walk / Activity",
  "Cultural Event",
  "Sport / Recreation",
  "Open Day",
  "Other",
];

export default function ClientFormModal({ client, onClose, onSaved }) {
  const isEdit = !!client?.id;
  const [form, setForm] = useState({
    first_name: "", last_name: "", date_of_birth: "", gender: "",
    email: "", phone: "", address: "", emergency_contact: "",
    team: "ndis",
    event_date: "", event_location: "", event_type: "", expected_attendees: 0,
    organiser_name: "", organiser_contact: "", partner_orgs: "",
    ndis_number: "", plan_start_date: "", plan_end_date: "", funding_type: "",
    primary_disability: "",
    billing_recipient: "", billing_recipient_name: "", billing_recipient_email: "",
    goals: "",
    assigned_to: "", status: "active",
    services: [], notes: "",
    ...client,
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEvent = form.team === "community";

  useEffect(() => {
    axios.get(`${API}/users/assignable`).then((r) => setUsers(r.data || [])).catch((e) => {
      console.error("Failed to load assignable users:", e);
    });
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) => setForm((f) => ({
    ...f,
    services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // For community events, last_name is optional (free text descriptor). Auto-fill if blank.
      const payload = { ...form };
      if (isEvent && !payload.last_name) payload.last_name = "Event";
      payload.expected_attendees = Number(payload.expected_attendees) || 0;
      if (isEdit) {
        await axios.put(`${API}/clients/${client.id}`, payload);
      } else {
        await axios.post(`${API}/clients`, payload);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save. Please check the fields.");
    } finally {
      setSaving(false);
    }
  };

  const recordLabel = isEvent ? "event" : "client";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="client-form-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE] sticky top-0 bg-white z-10">
          <h2 className="font-display text-2xl text-[#1F2A2E]">
            {isEdit ? `Edit ${recordLabel}` : `Add a new ${recordLabel}`}
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="modal-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-7 space-y-6" data-testid="client-form">
          <Section title="Team category">
            <Field label={isEvent ? "Which stream is this record under?" : "Which team is this client under?"} required>
              <select
                className="input-navica"
                value={form.team}
                onChange={(e) => update("team", e.target.value)}
                required
                data-testid="cf-team"
              >
                {TEAM_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
          </Section>

          {/* ── COMMUNITY EVENT SECTIONS ─────────────────────────────────── */}
          {isEvent ? (
            <>
              <Section title="Event details" icon={CalendarDays} data-testid="cf-event-details">
                <Field label="Event name" required>
                  <input
                    className="input-navica"
                    value={form.first_name}
                    onChange={(e) => update("first_name", e.target.value)}
                    placeholder="e.g. Wellbeing BBQ, Parramatta Park"
                    required
                    data-testid="cf-event-name"
                  />
                </Field>
                <Row>
                  <Field label="Event date" required>
                    <input
                      type="date"
                      className="input-navica"
                      value={form.event_date}
                      onChange={(e) => update("event_date", e.target.value)}
                      required={isEvent}
                      data-testid="cf-event-date"
                    />
                  </Field>
                  <Field label="Event type">
                    <select className="input-navica" value={form.event_type} onChange={(e) => update("event_type", e.target.value)} data-testid="cf-event-type">
                      <option value="">Select type...</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </Row>
                <Field label="Location / venue">
                  <input
                    className="input-navica"
                    value={form.event_location}
                    onChange={(e) => update("event_location", e.target.value)}
                    placeholder="Venue name, suburb"
                    data-testid="cf-event-location"
                  />
                </Field>
                <Row>
                  <Field label="Expected attendees">
                    <input
                      type="number"
                      min="0"
                      className="input-navica"
                      value={form.expected_attendees}
                      onChange={(e) => update("expected_attendees", e.target.value)}
                      data-testid="cf-expected-attendees"
                    />
                  </Field>
                  <Field label="Short descriptor (optional)">
                    <input
                      className="input-navica"
                      value={form.last_name === "Event" ? "" : form.last_name}
                      onChange={(e) => update("last_name", e.target.value)}
                      placeholder="e.g. NAIDOC Week, R U OK Day"
                      data-testid="cf-event-descriptor"
                    />
                  </Field>
                </Row>
              </Section>

              <Section title="Organiser & partners" icon={Users}>
                <Row>
                  <Field label="Organiser name">
                    <input className="input-navica" value={form.organiser_name} onChange={(e) => update("organiser_name", e.target.value)} placeholder="Lead Navica staff or partner" data-testid="cf-organiser-name" />
                  </Field>
                  <Field label="Organiser contact">
                    <input className="input-navica" value={form.organiser_contact} onChange={(e) => update("organiser_contact", e.target.value)} placeholder="Email or phone" data-testid="cf-organiser-contact" />
                  </Field>
                </Row>
                <Field label="Partner organisations">
                  <textarea
                    rows={2}
                    className="input-navica"
                    value={form.partner_orgs}
                    onChange={(e) => update("partner_orgs", e.target.value)}
                    placeholder="e.g. Headspace Parramatta, Western Sydney PHN, Lifeline"
                    data-testid="cf-partner-orgs"
                  />
                </Field>
                <Field label="Event goals / desired outcomes">
                  <textarea rows={3} className="input-navica" value={form.goals} onChange={(e) => update("goals", e.target.value)} placeholder="What is this event trying to achieve?" data-testid="cf-event-goals" />
                </Field>
              </Section>

              <Section title="Funding / billing (if applicable)">
                <Field label="Funded by">
                  <select
                    className="input-navica"
                    value={form.billing_recipient || ""}
                    onChange={(e) => update("billing_recipient", e.target.value)}
                    data-testid="cf-billing-recipient"
                  >
                    {EVENT_BILLING_OPTIONS.map((b) => <option key={b.value || "none"} value={b.value}>{b.label}</option>)}
                  </select>
                </Field>
                {form.billing_recipient && (
                  <Row>
                    <Field label="Funder name">
                      <input
                        className="input-navica"
                        value={form.billing_recipient_name || ""}
                        onChange={(e) => update("billing_recipient_name", e.target.value)}
                        placeholder="Grant body / sponsor name"
                        data-testid="cf-billing-recipient-name"
                      />
                    </Field>
                    <Field label="Funder email">
                      <input
                        type="email"
                        className="input-navica"
                        value={form.billing_recipient_email || ""}
                        onChange={(e) => update("billing_recipient_email", e.target.value)}
                        placeholder="invoices@funder.org"
                        data-testid="cf-billing-recipient-email"
                      />
                    </Field>
                  </Row>
                )}
              </Section>
            </>
          ) : (
            <>
              {/* ── REGULAR CLIENT SECTIONS ─────────────────────────────── */}
              <Section title="Personal">
                <Row>
                  <Field label="First name" required>
                    <input className="input-navica" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="cf-first-name" />
                  </Field>
                  <Field label="Last name" required>
                    <input className="input-navica" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required data-testid="cf-last-name" />
                  </Field>
                </Row>
                <Row>
                  <Field label="Date of birth"><input type="date" className="input-navica" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} /></Field>
                  <Field label="Gender">
                    <select className="input-navica" value={form.gender} onChange={(e) => update("gender", e.target.value)}>
                      <option value="">Select...</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
                    </select>
                  </Field>
                </Row>
                <Row>
                  <Field label="Phone"><input className="input-navica" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                  <Field label="Email"><input type="email" className="input-navica" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
                </Row>
                <Field label="Address"><input className="input-navica" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Street, suburb, postcode" /></Field>
                <Field label="Emergency contact"><input className="input-navica" value={form.emergency_contact} onChange={(e) => update("emergency_contact", e.target.value)} placeholder="Name, relationship, phone" /></Field>
              </Section>

              {form.team === "ndis" && (
                <Section title="NDIS">
                  <Row>
                    <Field label="NDIS number"><input className="input-navica" value={form.ndis_number} onChange={(e) => update("ndis_number", e.target.value)} /></Field>
                    <Field label="Funding type">
                      <select className="input-navica" value={form.funding_type} onChange={(e) => update("funding_type", e.target.value)}>
                        <option value="">Select...</option>
                        <option value="self">Self managed</option>
                        <option value="plan">Plan managed</option>
                        <option value="agency">NDIA managed</option>
                      </select>
                    </Field>
                  </Row>
                  <Row>
                    <Field label="Plan start"><input type="date" className="input-navica" value={form.plan_start_date} onChange={(e) => update("plan_start_date", e.target.value)} /></Field>
                    <Field label="Plan end"><input type="date" className="input-navica" value={form.plan_end_date} onChange={(e) => update("plan_end_date", e.target.value)} /></Field>
                  </Row>
                  <Field label="Primary disability"><input className="input-navica" value={form.primary_disability} onChange={(e) => update("primary_disability", e.target.value)} /></Field>
                </Section>
              )}

              <Section title="Goals & priorities">
                <Field label="Goals"><textarea rows={3} className="input-navica" value={form.goals} onChange={(e) => update("goals", e.target.value)} placeholder="Participant's personal goals..." /></Field>
              </Section>

              <Section title="Billing recipient (for invoices)">
                <Field label="Who is billed for this client?">
                  <select
                    className="input-navica"
                    value={form.billing_recipient || ""}
                    onChange={(e) => update("billing_recipient", e.target.value)}
                    data-testid="cf-billing-recipient"
                  >
                    {BILLING_RECIPIENT_OPTIONS.map((b) => (
                      <option key={b.value || "none"} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                </Field>
                {form.billing_recipient && form.billing_recipient !== "self_managed" && (
                  <Row>
                    <Field label="Recipient name (e.g. Plan Manager business name)">
                      <input
                        className="input-navica"
                        value={form.billing_recipient_name || ""}
                        onChange={(e) => update("billing_recipient_name", e.target.value)}
                        placeholder={form.billing_recipient === "ndia" ? "NDIA" : "Business name"}
                        data-testid="cf-billing-recipient-name"
                      />
                    </Field>
                    <Field label="Recipient email">
                      <input
                        type="email"
                        className="input-navica"
                        value={form.billing_recipient_email || ""}
                        onChange={(e) => update("billing_recipient_email", e.target.value)}
                        placeholder="invoices@example.com"
                        data-testid="cf-billing-recipient-email"
                      />
                    </Field>
                  </Row>
                )}
              </Section>

              <Section title="Services requested">
                <div className="grid sm:grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map((s) => {
                    const checked = form.services.includes(s);
                    return (
                      <label key={s} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer text-sm ${checked ? "bg-[#FCE7EB] border-[#E2718A]" : "bg-white border-[#F0E5DE]"}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleService(s)} className="navica-check" />
                        <span className="text-[#284952]">{s}</span>
                      </label>
                    );
                  })}
                </div>
              </Section>
            </>
          )}

          {/* ── ALWAYS-SHOWN: assignment & status ─────────────────────── */}
          <Section title={isEvent ? "Event management" : "Case management"}>
            <Row>
              <Field label={isEvent ? "Assigned lead" : "Assigned case worker"}>
                <select className="input-navica" value={form.assigned_to} onChange={(e) => update("assigned_to", e.target.value)} data-testid="cf-assigned-to">
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className="input-navica" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="cf-status">
                  <option value="active">{isEvent ? "Planned / active" : "Active"}</option>
                  <option value="on_hold">{isEvent ? "Postponed" : "On hold"}</option>
                  <option value="inactive">{isEvent ? "Completed / cancelled" : "Inactive"}</option>
                </select>
              </Field>
            </Row>
            <Field label="Summary notes"><textarea rows={3} className="input-navica" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Quick summary..." /></Field>
          </Section>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#F0E5DE]">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="cf-submit">
              {saving ? "Saving..." : isEdit ? "Save changes" : `Add ${recordLabel}`} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, ...rest }) {
  return (
    <div className="border-t border-[#F0E5DE] pt-6 first:border-0 first:pt-0" {...rest}>
      <h3 className="font-display text-lg text-[#1F2A2E] mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-[#E2718A]" />}
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">{label} {required && <span className="text-[#E2718A]">*</span>}</label>
      {children}
    </div>
  );
}
